import React from 'react';
import { Card } from '../components/ui/card';
import { FileText, Scale, AlertCircle, CheckCircle, Ban, CreditCard, Home, Shield } from 'lucide-react';

export const TermsPage = () => {
  const lastUpdated = "January 1, 2026";
  
  return (
    <div className="min-h-screen bg-background pt-24 pb-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-primary/10">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-display font-bold text-foreground">
                Terms of Service
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Welcome to Urban Oasis Cascais. These Terms of Service ("Terms") govern your use of our website and services operated by Urban Oasis LLC ("we," "us," or "our"). By accessing our website or making a booking inquiry, you agree to these Terms.
          </p>
        </div>

        {/* Important Notice */}
        <Card className="p-6 mb-8 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
          <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            Important Notice
          </h2>
          <p className="text-sm text-muted-foreground">
            Bookings may be made directly through our property management partner, Propr, or through third-party platforms such as Airbnb, VRBO, or Booking.com. When booking through third-party platforms, their respective terms of service also apply.
          </p>
        </Card>

        {/* Sections */}
        <div className="space-y-8">
          {/* Section 1 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-primary" />
              1. Acceptance of Terms
            </h2>
            <Card className="p-6 bg-card border-border">
              <div className="space-y-4 text-muted-foreground">
                <p>
                  By accessing this website or submitting a booking inquiry, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy.
                </p>
                <p>
                  If you do not agree to these Terms, please do not use our website or services. We reserve the right to modify these Terms at any time, and your continued use of the website constitutes acceptance of any modifications.
                </p>
              </div>
            </Card>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4 flex items-center gap-2">
              <Home className="w-5 h-5 text-primary" />
              2. Property & Services
            </h2>
            <Card className="p-6 bg-card border-border">
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Urban Oasis Cascais is a short-term rental property located in Cascais, Portugal. The property is professionally managed by Propr.
                </p>
                <p className="font-medium text-foreground">Our services include:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Providing accurate information about the property and its amenities</li>
                  <li>Facilitating booking inquiries through our contact form</li>
                  <li>Directing guests to appropriate booking channels</li>
                  <li>Guest support through our property management partner</li>
                </ul>
                <p className="text-sm bg-muted/50 p-3 rounded-lg mt-4">
                  <strong>Disclaimer:</strong> While we strive to ensure all information on this website is accurate, descriptions and photographs are for illustrative purposes. Minor variations may exist.
                </p>
              </div>
            </Card>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              3. Bookings & Payments
            </h2>
            <Card className="p-6 bg-card border-border">
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Bookings can be made through the following channels:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Direct booking:</strong> Through our property manager, Propr</li>
                  <li><strong>Third-party platforms:</strong> Airbnb, VRBO, Booking.com, and other vacation rental platforms</li>
                </ul>
                <div className="mt-4 space-y-3">
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <strong className="text-foreground">Payment Terms:</strong> All payments, cancellation policies, and refund procedures are governed by the booking platform used or by direct agreement with Propr.
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <strong className="text-foreground">Security Deposit:</strong> A security deposit may be required and will be handled according to the booking platform's policies or direct agreement.
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <strong className="text-foreground">Cancellation:</strong> Cancellation policies vary by booking platform. Please review the applicable policy before confirming your reservation.
                  </div>
                </div>
              </div>
            </Card>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-primary" />
              4. Guest Responsibilities
            </h2>
            <Card className="p-6 bg-card border-border">
              <div className="space-y-4 text-muted-foreground">
                <p>As a guest, you agree to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide accurate and complete information when making inquiries or bookings</li>
                  <li>Treat the property with care and respect</li>
                  <li>Comply with all house rules provided upon booking confirmation</li>
                  <li>Respect neighbors and maintain appropriate noise levels</li>
                  <li>Not exceed the maximum occupancy stated in your booking</li>
                  <li>Report any damages or issues promptly to your Propr Host</li>
                  <li>Leave the property in a reasonable condition upon checkout</li>
                  <li>Comply with all applicable local laws and regulations</li>
                </ul>
              </div>
            </Card>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4 flex items-center gap-2">
              <Ban className="w-5 h-5 text-primary" />
              5. Prohibited Activities
            </h2>
            <Card className="p-6 bg-card border-border">
              <div className="space-y-4 text-muted-foreground">
                <p>The following activities are strictly prohibited:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Parties, events, or gatherings without prior written approval</li>
                  <li>Smoking inside the property</li>
                  <li>Illegal activities of any kind</li>
                  <li>Subletting or allowing unregistered guests</li>
                  <li>Commercial activities without prior approval</li>
                  <li>Bringing pets without prior approval (if applicable)</li>
                  <li>Tampering with security devices or property equipment</li>
                  <li>Any activity that disturbs neighbors or violates local ordinances</li>
                </ul>
                <p className="text-sm bg-red-50 dark:bg-red-950/20 p-3 rounded-lg mt-4 border border-red-200 dark:border-red-800">
                  <strong>Violation of these rules may result in immediate termination of your stay without refund and additional charges for damages or cleaning.</strong>
                </p>
              </div>
            </Card>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              6. Liability & Disclaimers
            </h2>
            <Card className="p-6 bg-card border-border">
              <div className="space-y-4 text-muted-foreground">
                <p>
                  <strong className="text-foreground">Limitation of Liability:</strong> To the maximum extent permitted by law, Urban Oasis LLC and its property management partner, Propr, shall not be liable for:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Any indirect, incidental, special, or consequential damages</li>
                  <li>Loss or theft of personal belongings</li>
                  <li>Personal injury occurring on the property (except where caused by our negligence)</li>
                  <li>Service interruptions due to utility failures or circumstances beyond our control</li>
                  <li>Actions of third parties or other guests</li>
                </ul>
                <p className="mt-4">
                  <strong className="text-foreground">Force Majeure:</strong> We shall not be liable for any failure to perform our obligations due to circumstances beyond our reasonable control, including but not limited to natural disasters, pandemics, government actions, or civil unrest.
                </p>
                <p>
                  <strong className="text-foreground">Travel Insurance:</strong> We strongly recommend that all guests obtain comprehensive travel insurance to protect against unforeseen circumstances.
                </p>
              </div>
            </Card>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4">
              7. Intellectual Property
            </h2>
            <Card className="p-6 bg-card border-border">
              <p className="text-muted-foreground">
                All content on this website, including text, images, graphics, logos, and design elements, is the property of Urban Oasis LLC or its licensors and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
              </p>
            </Card>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4">
              8. Governing Law & Disputes
            </h2>
            <Card className="p-6 bg-card border-border">
              <div className="space-y-4 text-muted-foreground">
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of Portugal and applicable European Union regulations, including the GDPR.
                </p>
                <p>
                  Any disputes arising from these Terms or your use of our services shall first be attempted to be resolved through good-faith negotiation. If resolution cannot be reached, disputes shall be submitted to the competent courts of Cascais, Portugal.
                </p>
              </div>
            </Card>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4">
              9. Severability
            </h2>
            <Card className="p-6 bg-card border-border">
              <p className="text-muted-foreground">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
            </Card>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4">
              10. Contact Information
            </h2>
            <Card className="p-6 bg-card border-border">
              <p className="text-muted-foreground">
                For questions about these Terms of Service, please contact us through our <a href="/contact" className="text-primary hover:underline">contact form</a> or reach out to your Propr Host for assistance.
              </p>
              <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Service Provider:</strong><br />
                  Property managed by <a href="https://propr.pt" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Propr</a>
                </p>
              </div>
            </Card>
          </section>
        </div>
      </div>
    </div>
  );
};
